/**
 * 角标素材配置
 *
 * 应用入口通过 useRemarkCorner() 预拉取；
 * 业务侧渲染统一用 RemarkCornerImg（内部订阅 map 更新）；
 * 非 React 或已确认 ready 后可用 getRemarkCorner 同步查询。
 * 请求走 widgets 内置 webApi（见 ./api）。
 */
import React, { useEffect, useState, useSyncExternalStore, type ImgHTMLAttributes } from 'react';
import { fetchRemarkCornerList, type RemarkCornerItem } from './api';

export { fetchRemarkCornerList, type RemarkCornerItem, type RemarkCornerListResult } from './api';

type RemarkCornerMap = Record<string, RemarkCornerItem>;

let remarkCornerMap: RemarkCornerMap = {};
/** 全局单例请求，避免并发重复拉取 */
let initPromise: Promise<RemarkCornerMap> | null = null;

const listeners = new Set<() => void>();

/**
 * 订阅角标 Map 变更
 *
 * @param listener - Map 更新时的回调
 * @returns 取消订阅函数
 */
const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/**
 * 获取当前角标 Map 快照（供 useSyncExternalStore）
 *
 * @returns 当前 keyword → 角标配置 Map
 */
const getSnapshot = () => remarkCornerMap;

/**
 * 通知所有订阅者 Map 已更新
 *
 * @sideEffect 触发 useSyncExternalStore 订阅方重渲染
 */
const notifyListeners = () => {
    listeners.forEach((listener) => listener());
};

type Keyword = 'svip' | 'vip' | 'vip-single' | 'limit-free' | 'beta-test';

/**
 * 按 keyword 同步取角标预览图 URL
 *
 * 仅适合非 React 或已确认 ready 后的一次性读取；
 * 组件 render 中请统一用 RemarkCornerImg，否则可能读到空缓存且不会随拉取完成更新。
 *
 * @param keyword - 角标 keyword
 * @returns 预览图 URL；未加载完成或不存在时返回 undefined
 */
export const getRemarkCorner = (keyword?: Keyword | string) => {
    if (!keyword) return undefined;
    return remarkCornerMap[keyword]?.preview?.url;
};

/**
 * 订阅角标 Map，按 keyword 取预览图 URL（拉取完成后自动重渲染）
 *
 * 供 RemarkCornerImg 内部使用；业务渲染请直接用 RemarkCornerImg。
 *
 * @param keyword - 角标 keyword
 * @returns 预览图 URL；未加载完成或不存在时返回 undefined
 */
export const useRemarkCornerUrl = (keyword?: Keyword | string): string | undefined => {
    const map = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    if (!keyword) return undefined;
    return map[keyword]?.preview?.url;
};

export type RemarkCornerImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
    /** 角标 keyword */
    keyword: Keyword | string;
};

/**
 * 角标图片：内部订阅 Map，适合非组件工厂函数里写 JSX 的场景
 *
 * @param props - keyword + 原生 img 属性（不含 src）
 * @returns 有 URL 时渲染 img，否则 null
 */
export const RemarkCornerImg = ({ keyword, alt = '', ...rest }: RemarkCornerImgProps): React.ReactElement | null => {
    const url = useRemarkCornerUrl(keyword);
    if (!url) return null;
    return <img src={url} alt={alt} {...rest} />;
};

/**
 * 初始化角标配置（全局单例；失败后可再次调用重试）
 *
 * @returns keyword → 角标配置 Map
 *
 * @sideEffect 成功写入后通知订阅者
 */
export const initRemarkCorner = () => {
    if (initPromise) return initPromise;

    initPromise = (async () => {
        try {
            const { code, data } = await fetchRemarkCornerList();
            if (code !== 0) {
                initPromise = null;
                return remarkCornerMap;
            }

            const list = data?.data ?? [];
            remarkCornerMap = list.reduce<RemarkCornerMap>((acc, item) => {
                const key = item?.keyword;
                if (key) {
                    acc[key] = item;
                }
                return acc;
            }, {});
            notifyListeners();
            return remarkCornerMap;
        } catch {
            // 网络异常允许后续重试
            initPromise = null;
            return remarkCornerMap;
        }
    })();

    return initPromise;
};

/**
 * 在应用根组件挂载，预拉取角标列表
 *
 * @returns ready 是否已结束首次拉取；remarkCornerMap 当前角标 Map
 */
export const useRemarkCorner = () => {
    const map = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const [ready, setReady] = useState(() => Object.keys(remarkCornerMap).length > 0);

    useEffect(() => {
        let cancelled = false;

        initRemarkCorner().then(() => {
            if (cancelled) return;
            setReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, []);

    return {
        ready,
        remarkCornerMap: map,
    };
};
